import { IconSelector } from "@tabler/icons-react";
import { useMemo, type ReactNode } from "react";
import { Button, Select } from "react-aria-components";
import type { Size } from "../helpers/helpers.js";
import { MultiSelectPopover } from "./multiSelectPopover.js";
import { selectStyle } from "./style.js";

export interface MultiSelectOption {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Indentation level. When set, the option is rendered with left padding and an arrow icon. */
  indent?: number;
}

export interface MultiSelectSection {
  id: string;
  /** Optional section label. If omitted, no section header is rendered. */
  label?: string;
  options: readonly MultiSelectOption[];
}

export interface MultiSelectProps {
  leftAccessory?: ReactNode;
  label?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  sections: readonly MultiSelectSection[];
  value: readonly string[];
  onChange: (value: readonly string[]) => void;
}

export const MultiSelect = (props: MultiSelectProps) => {
  const value = useMemo(() => [...props.value], [props.value]);

  const style = selectStyle({
    isInvalid: props.isInvalid,
    size: props.size,
  });

  return (
    <Select
      className="contents"
      aria-label={props.label ?? "Select options"}
      selectionMode="multiple"
      value={value}
      onChange={(value) => {
        props.onChange(value.filter((id) => typeof id === "string"));
      }}
      isDisabled={props.isDisabled}
      isInvalid={props.isInvalid}
      placeholder={props.placeholder ?? "Select options"}
    >
      <Button className={style.button()}>
        {props.leftAccessory ? (
          <div className="shrink-0 text-gray-700">{props.leftAccessory}</div>
        ) : null}
        {props.label ? (
          <span className={style.value()}>{props.label}</span>
        ) : (
          <span className={style.placeholder()}>{props.placeholder}</span>
        )}
        <IconSelector className={style.chevron()} />
      </Button>
      <MultiSelectPopover sections={props.sections} />
    </Select>
  );
};

export function findMultiSelectOptionId(
  options: readonly MultiSelectSection[],
  predicate: (option: MultiSelectOption) => boolean,
): string | undefined {
  return options.reduce<string | undefined>(
    (foundId, section) => foundId ?? section.options.find(predicate)?.id,
    undefined,
  );
}

export function getMultiSelectOptionLabel(
  options: readonly MultiSelectSection[],
  selectedId: string | undefined,
): string | undefined {
  if (!selectedId) {
    return undefined;
  }

  const option = options.reduce<MultiSelectOption | undefined>(
    (foundOption, section) =>
      foundOption ?? section.options.find((option) => option.id === selectedId),
    undefined,
  );

  return option?.label;
}
